import { useEffect, useRef, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import {
  isAgentQuestionFreeTextOption,
} from '@/features/chat/agentQuestion/resolveAgentQuestionOptionSendText.js'
import styles from './agentQuestionBlock.module.scss'

export default function AgentQuestionBlock({
  prompt,
  options = [],
  status = 'open',
  interactive = false,
  onSelect,
  onConfirm,
  freeTextOptionId = 'd',
}) {
  const isOpen = status === 'open'
  const isFreeTextOption = (optionId) => isAgentQuestionFreeTextOption(optionId, { freeTextOptionId })
  const [selectedOptionId, setSelectedOptionId] = useState(/** @type {string | null} */ (null))
  const [freeText, setFreeText] = useState('')
  const freeTextInputRef = useRef(/** @type {HTMLInputElement | null} */ (null))

  useEffect(() => {
    if (interactive && selectedOptionId && isFreeTextOption(selectedOptionId)) {
      freeTextInputRef.current?.focus()
    }
  }, [interactive, selectedOptionId, isFreeTextOption])

  const canConfirm = (() => {
    if (!selectedOptionId) return false
    if (isFreeTextOption(selectedOptionId)) {
      return freeText.trim().length > 0
    }
    return true
  })()

  const handleOptionPick = (optionId) => {
    if (!isOpen) return
    setSelectedOptionId(optionId)
    if (interactive) return
    onSelect?.(optionId)
  }

  const handleConfirmClick = () => {
    if (!canConfirm || !selectedOptionId) return
    onConfirm?.({
      optionId: selectedOptionId,
      freeText: isFreeTextOption(selectedOptionId) ? freeText.trim() : undefined,
    })
  }

  return (
    <Box className={styles.panel}>
      <Typography component="p" className={styles.prompt}>
        {prompt}
      </Typography>
      <Box component="ul" className={styles.options}>
        {options.map((option) => {
          const isFreeText = isFreeTextOption(option.id)
          const isSelected = selectedOptionId === option.id
          return (
            <Box component="li" key={option.id}>
              <Box
                component="button"
                type="button"
                className={`${styles.optionRow}${isSelected ? ` ${styles.optionRowSelected}` : ''}`}
                disabled={!isOpen || (!interactive && !onSelect)}
                onClick={() => handleOptionPick(option.id)}
                aria-pressed={isSelected}
              >
                <span className={styles.letterBox} aria-hidden>
                  {option.letter}
                </span>
                {isFreeText && interactive && isSelected ? (
                  <input
                    ref={freeTextInputRef}
                    type="text"
                    className={styles.freeTextInput}
                    value={freeText}
                    placeholder="Type here"
                    disabled={!isOpen}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setFreeText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && canConfirm) {
                        e.preventDefault()
                        handleConfirmClick()
                      }
                    }}
                    aria-label="Other — type your answer"
                  />
                ) : (
                  <span
                    className={`${styles.optionLabel}${isFreeText ? ` ${styles.optionLabelFreeText}` : ''}`}
                  >
                    {isFreeText ? 'Other' : option.label}
                  </span>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
      {interactive && isOpen ? (
        <Box className={styles.confirmActions}>
          <Button
            type="button"
            variant="contained"
            size="small"
            className={styles.confirmButton}
            disabled={!canConfirm}
            onClick={handleConfirmClick}
          >
            Confirm
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}
